import React, { useCallback } from 'react';
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinkedAccount } from '@/types/residents/home';
import { LINKED_ACCOUNTS_COLORS } from '@/constants/residents/mobile/linkedAccountsColors';
import { MobileLinkedAccountCard } from './MobileLinkedAccountCard';
import { MobileLinkedAccountsEmpty } from './MobileLinkedAccountsEmpty';

interface MobileLinkedAccountsListProps {
  accounts: LinkedAccount[];
  isLoading: boolean;
  onRefresh: () => void;
  onRemove: (account: LinkedAccount) => void;
  onEmailPress?: (email: string) => void;
}

/**
 * Mobile Linked Accounts List Component
 * FlatList with dark theme and pull-to-refresh
 */
export const MobileLinkedAccountsList: React.FC<MobileLinkedAccountsListProps> = ({
  accounts,
  isLoading,
  onRefresh,
  onRemove,
  onEmailPress,
}) => {
  const insets = useSafeAreaInsets();

  const renderItem = useCallback(
    ({ item }: { item: LinkedAccount }) => (
      <MobileLinkedAccountCard
        account={item}
        onRemove={onRemove}
        onEmailPress={onEmailPress}
      />
    ),
    [onRemove, onEmailPress]
  );

  const keyExtractor = useCallback(
    (item: LinkedAccount) => String(item.id),
    []
  );

  const renderEmptyComponent = useCallback(
    () => <MobileLinkedAccountsEmpty />,
    []
  );

  return (
    <FlatList
      data={accounts}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      style={styles.list}
      contentContainerStyle={[
        styles.listContent,
        { paddingBottom: 24 + insets.bottom },
        accounts.length === 0 && styles.emptyListContent,
      ]}
      refreshControl={
        <RefreshControl
          refreshing={isLoading}
          onRefresh={onRefresh}
          tintColor={LINKED_ACCOUNTS_COLORS.accentBlue}
        />
      }
      ListEmptyComponent={renderEmptyComponent}
      showsVerticalScrollIndicator={false}
      scrollEventThrottle={16}
      decelerationRate="normal"
      // Performance optimizations
      removeClippedSubviews={true}
      maxToRenderPerBatch={10}
      windowSize={5}
      initialNumToRender={10}
    />
  );
};

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 24,
  },
  emptyListContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
});

